// src/components/order/OrdersSearchPanel.tsx
//
// OrdersSearchPanel
// - Presentation-only search UI for Orders page.
// - Delegates all state ownership to the parent (Orders.tsx).
// - Uses the shared SearchPanel container for:
//   - Search / Reset buttons (single source of truth)
//   - Optional auto-search (debounced) driven by parent-provided `autoSearch`
//   - Active filter chips (removable, with readable labels)
//
// IMPORTANT:
// - Do NOT hardcode autoSearch defaults here.
//   SearchPanel has global defaults (enabled: false, delayMs: 500) via constants.
//   This component should only pass through `autoSearch` if provided by the page.

import React, { useMemo } from 'react';
import { Grid } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';

import {
  SearchPanel,
  SearchTextField,
  SearchDateRange,
  type ActiveChipsConfig,
  type SearchChipSpec,
  type SearchPanelAction,
  type SetSearchValueFn,
} from '@components/common/search';

import { OrderStatus } from '@models/order';
import { ProductSaleType } from '@apis/product';
import { UserGroup, type User } from '@models/user';
import { dateToTimestamp, timestampToDate } from '@utils/time';

import type { GetOrdersParams } from '@apis/order';
import type { SelectValue } from '@components/common/Select';

import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ListAltIcon from '@mui/icons-material/ListAlt';

import { ORDER_STATUS_LABELS, PRODUCT_SALE_TYPE_LABELS } from '@constants/orderLabels';

type OrdersSearchForm = GetOrdersParams;

type Option = {
  label: string;
  value: SelectValue;
};

type AutoSearchConfig = {
  enabled?: boolean;
  delayMs?: number;
};

interface Props {
  searchForm: OrdersSearchForm;
  onChangeInput: (key: keyof OrdersSearchForm, value: string | number | null) => void;

  onSearch: () => void;
  onReset: () => void;

  stockOptions: Option[];
  partnerOptions: Option[];
  user: User | null | undefined;

  isExporting: boolean;
  onExport: () => void;
  onExportReport: () => void;
  onClickCreate?: () => void;

  /**
   * Auto-search configuration provided by Orders.tsx.
   * If omitted, SearchPanel falls back to its global defaults.
   */
  autoSearch?: AutoSearchConfig;
}

/* ------------------------------------------------------------------ */
/* Static options (sorted by labels)                                   */
/* ------------------------------------------------------------------ */

const ORDER_STATUS_OPTIONS: Option[] = Object.values(OrderStatus)
  .slice()
  .sort((a, b) => ORDER_STATUS_LABELS[a].localeCompare(ORDER_STATUS_LABELS[b]))
  .map((v) => ({ value: v, label: ORDER_STATUS_LABELS[v] }));

const PRODUCT_SALE_TYPE_OPTIONS: Option[] = Object.values(ProductSaleType)
  .slice()
  .sort((a, b) => PRODUCT_SALE_TYPE_LABELS[a].localeCompare(PRODUCT_SALE_TYPE_LABELS[b]))
  .map((v) => ({ value: v, label: PRODUCT_SALE_TYPE_LABELS[v] }));

const findOption = (options: Option[], value?: SelectValue | null) =>
  options.find((o) => o.value === value) ?? null;

/**
 * Normalize MUI Autocomplete option value into the page-level input shape.
 * (Orders.tsx expects string | number | null.)
 */
const optionValueToInput = (opt: Option | null): string | number | null =>
  (opt?.value as unknown as string | number | null) ?? null;

const OrdersSearchPanel: React.FC<Props> = ({
  searchForm,
  onChangeInput,
  onSearch,
  onReset,

  stockOptions,
  partnerOptions,
  user,

  isExporting,
  onExport,
  onExportReport,
  onClickCreate,

  autoSearch,
}) => {
  const isPartner = user?.group === UserGroup.PARTNER;

  // Partner view hides Stock/Partner selectors (scope is fixed by session).
  const showStock = !isPartner;
  const showPartner = !isPartner;

  /* ------------------------------------------------------------------ */
  /* Only pass autoSearch when explicitly enabled (policy)               */
  /* ------------------------------------------------------------------ */

  const panelAutoSearch = autoSearch?.enabled === true ? autoSearch : undefined;

  /* ------------------------------------------------------------------ */
  /* Selected options (Autocomplete value)                               */
  /* ------------------------------------------------------------------ */

  const selectedStatus = useMemo(
    () => findOption(ORDER_STATUS_OPTIONS, searchForm.orderStatus),
    [searchForm.orderStatus],
  );

  const selectedSaleType = useMemo(
    () => findOption(PRODUCT_SALE_TYPE_OPTIONS, searchForm.productSaleType),
    [searchForm.productSaleType],
  );

  const selectedStock = useMemo(
    () => findOption(stockOptions, searchForm.stockId),
    [stockOptions, searchForm.stockId],
  );

  /**
   * Partner users are scoped to their own partnerId.
   * (The selector UI is hidden, but we may still show a chip depending on policy.)
   */
  const partnerIdForSelect = isPartner ? user?.partnerId : searchForm.partnerId;

  const selectedPartner = useMemo(
    () => findOption(partnerOptions, partnerIdForSelect as SelectValue | null),
    [partnerOptions, partnerIdForSelect],
  );

  /* ------------------------------------------------------------------ */
  /* Lookup maps for chips (O(1) label access)                           */
  /* ------------------------------------------------------------------ */

  const stockLabelById = useMemo(() => {
    const map = new Map<number, string>();
    stockOptions.forEach((o) => map.set(Number(o.value), o.label));
    return map;
  }, [stockOptions]);

  const partnerLabelById = useMemo(() => {
    const map = new Map<number, string>();
    partnerOptions.forEach((o) => map.set(Number(o.value), o.label));
    return map;
  }, [partnerOptions]);

  /* ------------------------------------------------------------------ */
  /* SearchPanel actions (Export / Export Details / Create)              */
  /* ------------------------------------------------------------------ */

  const actions: SearchPanelAction[] = useMemo(() => {
    const list: SearchPanelAction[] = [
      {
        key: 'export',
        label: 'Export',
        startIcon: <FileDownloadIcon />,
        onClick: onExport,
        disabled: isExporting,
        variant: 'outlined',
      },
      {
        key: 'export-detail',
        label: 'Export Details',
        startIcon: <ListAltIcon />,
        onClick: onExportReport,
        disabled: isExporting,
        variant: 'outlined',
      },
    ];

    if (onClickCreate) {
      list.push({
        key: 'create',
        label: 'Create',
        onClick: onClickCreate,
        variant: 'contained',
      });
    }

    return list;
  }, [isExporting, onClickCreate, onExport, onExportReport]);

  /* ------------------------------------------------------------------ */
  /* Active chips configuration                                          */
  /* ------------------------------------------------------------------ */

  const chipsConfig: ActiveChipsConfig<OrdersSearchForm> = useMemo(() => {
    const setValue: SetSearchValueFn<OrdersSearchForm> = (key, value) => {
      onChangeInput(key, (value as unknown as string | number | null) ?? null);
    };

    const chips: SearchChipSpec<OrdersSearchForm>[] = [
      { key: 'orderCode', label: 'Order Code', clear: ({ setValue }) => setValue('orderCode', '') },
      { key: 'iccid', label: 'ICCID', clear: ({ setValue }) => setValue('iccid', null) },
      { key: 'passportNumber', label: 'Passport', clear: ({ setValue }) => setValue('passportNumber', null) },

      { key: 'productCode', label: 'Product Code', clear: ({ setValue }) => setValue('productCode', '') },
      { key: 'productName', label: 'Product Name', clear: ({ setValue }) => setValue('productName', '') },

      {
        key: 'productSaleType',
        label: 'Order Type',
        isActive: (s) => Boolean(s.productSaleType),
        getValueText: (s) => {
          const v = s.productSaleType as ProductSaleType | undefined;
          if (!v) return '';
          return PRODUCT_SALE_TYPE_LABELS[v] ?? String(v);
        },
        clear: ({ setValue }) => setValue('productSaleType', null),
      },
      {
        key: 'orderStatus',
        label: 'Order State',
        isActive: (s) => Boolean(s.orderStatus),
        getValueText: (s) => {
          const v = s.orderStatus as OrderStatus | undefined;
          if (!v) return '';
          return ORDER_STATUS_LABELS[v] ?? String(v);
        },
        clear: ({ setValue }) => setValue('orderStatus', null),
      },

      {
        key: 'createdAtFrom',
        label: 'Order Date From',
        getValueText: (s) => (s.createdAtFrom ? timestampToDate(s.createdAtFrom, 'YYYY-MM-DD') : ''),
        clear: ({ setValue }) => setValue('createdAtFrom', null),
      },
      {
        key: 'createdAtTo',
        label: 'Order Date To',
        getValueText: (s) => (s.createdAtTo ? timestampToDate(s.createdAtTo, 'YYYY-MM-DD') : ''),
        clear: ({ setValue }) => setValue('createdAtTo', null),
      },
      {
        key: 'reservedAtFrom',
        label: 'Reserved From',
        getValueText: (s) => (s.reservedAtFrom ? timestampToDate(s.reservedAtFrom, 'YYYY-MM-DD') : ''),
        clear: ({ setValue }) => setValue('reservedAtFrom', null),
      },
      {
        key: 'reservedAtTo',
        label: 'Reserved To',
        getValueText: (s) => (s.reservedAtTo ? timestampToDate(s.reservedAtTo, 'YYYY-MM-DD') : ''),
        clear: ({ setValue }) => setValue('reservedAtTo', null),
      },

      { key: 'requestOrderId', label: 'Partner Order ID', clear: ({ setValue }) => setValue('requestOrderId', null) },
      { key: 'externalOrderId', label: 'Supplier Order ID', clear: ({ setValue }) => setValue('externalOrderId', null) },

      {
        key: 'stockId',
        label: 'Stock',
        getValueText: (s) => (s.stockId ? stockLabelById.get(Number(s.stockId)) ?? String(s.stockId) : ''),
        clear: ({ setValue }) => setValue('stockId', null),
        removable: () => showStock,
      },
      {
        key: 'partnerId',
        label: 'Partner',
        getValueText: (s) => (s.partnerId ? partnerLabelById.get(Number(s.partnerId)) ?? String(s.partnerId) : ''),
        clear: ({ setValue }) => setValue('partnerId', null),
        removable: () => showPartner,
      },
    ];

    return {
      enabled: true,
      search: searchForm,
      setValue,
      // After chip removal, immediately trigger a search to reflect the cleared filter.
      onAfterClear: onSearch,
      chips,
    };
  }, [onChangeInput, onSearch, partnerLabelById, searchForm, showPartner, showStock, stockLabelById]);

  /* ------------------------------------------------------------------ */
  /* Render                                                             */
  /* ------------------------------------------------------------------ */

  return (
    <SearchPanel
      title="Filters"
      onSearch={onSearch}
      onReset={onReset}
      actions={actions}
      // Pass-through only when enabled. Otherwise use SearchPanel global defaults.
      autoSearch={panelAutoSearch}
      chipsConfig={chipsConfig}
    >
      <Grid container spacing={2}>
        {/* Row 1: Order Code, ICCID, Passport, Order State */}
        <Grid item xs={12} sm={6} md={3}>
          <SearchTextField
            label="Order Code"
            size="small"
            fullWidth
            value={searchForm.orderCode || ''}
            onChangeValue={(v) => onChangeInput('orderCode', v)}
            onClear={() => onChangeInput('orderCode', '')}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <SearchTextField
            label="ICCID"
            size="small"
            fullWidth
            value={searchForm.iccid ?? ''}
            onChangeValue={(v) => onChangeInput('iccid', v)}
            onClear={() => onChangeInput('iccid', null)}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <SearchTextField
            label="Passport Number"
            size="small"
            fullWidth
            value={searchForm.passportNumber ?? ''}
            onChangeValue={(v) => onChangeInput('passportNumber', v)}
            onClear={() => onChangeInput('passportNumber', null)}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Autocomplete
            disablePortal
            options={ORDER_STATUS_OPTIONS}
            value={selectedStatus}
            onChange={(_, v) => onChangeInput('orderStatus', optionValueToInput(v))}
            getOptionLabel={(opt) => String(opt.label ?? '')}
            isOptionEqualToValue={(a, b) => a.value === b.value}
            renderInput={(params) => (
              <SearchTextField
                {...params}
                label="Order State"
                size="small"
                fullWidth
                onClear={() => onChangeInput('orderStatus', null)}
              />
            )}
          />
        </Grid>

        {/* Row 2: Product Code, Product Name, Order Type, Stock Code (admin only) */}
        <Grid item xs={12} sm={6} md={3}>
          <SearchTextField
            label="Product Code"
            size="small"
            fullWidth
            value={searchForm.productCode || ''}
            onChangeValue={(v) => onChangeInput('productCode', v)}
            onClear={() => onChangeInput('productCode', '')}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <SearchTextField
            label="Product Name"
            size="small"
            fullWidth
            value={searchForm.productName || ''}
            onChangeValue={(v) => onChangeInput('productName', v)}
            onClear={() => onChangeInput('productName', '')}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Autocomplete
            disablePortal
            options={PRODUCT_SALE_TYPE_OPTIONS}
            value={selectedSaleType}
            onChange={(_, v) => onChangeInput('productSaleType', optionValueToInput(v))}
            getOptionLabel={(opt) => String(opt.label ?? '')}
            isOptionEqualToValue={(a, b) => a.value === b.value}
            renderInput={(params) => (
              <SearchTextField
                {...params}
                label="Order Type"
                size="small"
                fullWidth
                onClear={() => onChangeInput('productSaleType', null)}
              />
            )}
          />
        </Grid>

        {showStock ? (
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              disablePortal
              options={stockOptions}
              value={selectedStock}
              onChange={(_, v) => onChangeInput('stockId', optionValueToInput(v))}
              getOptionLabel={(opt) => String(opt.label ?? '')}
              isOptionEqualToValue={(a, b) => a.value === b.value}
              renderInput={(params) => (
                <SearchTextField
                  {...params}
                  label="Stock Code"
                  size="small"
                  fullWidth
                  onClear={() => onChangeInput('stockId', null)}
                />
              )}
            />
          </Grid>
        ) : (
          <Grid item xs={12} sm={6} md={3} />
        )}

        {/* Row 3: Date ranges */}
        <Grid item xs={12} md={6}>
          <SearchDateRange
            label="Order Date"
            from={searchForm.createdAtFrom ? timestampToDate(searchForm.createdAtFrom, 'YYYY-MM-DD') : ''}
            to={searchForm.createdAtTo ? timestampToDate(searchForm.createdAtTo, 'YYYY-MM-DD') : ''}
            onChangeFrom={(v) => onChangeInput('createdAtFrom', v ? dateToTimestamp(v, false, true, true) : null)}
            onChangeTo={(v) => onChangeInput('createdAtTo', v ? dateToTimestamp(v, true, true) : null)}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <SearchDateRange
            label="Reserved Date"
            from={searchForm.reservedAtFrom ? timestampToDate(searchForm.reservedAtFrom, 'YYYY-MM-DD') : ''}
            to={searchForm.reservedAtTo ? timestampToDate(searchForm.reservedAtTo, 'YYYY-MM-DD') : ''}
            onChangeFrom={(v) => onChangeInput('reservedAtFrom', v ? dateToTimestamp(v, false, true, true) : null)}
            onChangeTo={(v) => onChangeInput('reservedAtTo', v ? dateToTimestamp(v, true, true) : null)}
          />
        </Grid>

        {/* Row 4: Partner Order ID, Supplier Order ID, Partner (admin only) */}
        <Grid item xs={12} sm={6} md={3}>
          <SearchTextField
            label="Partner Order ID"
            size="small"
            fullWidth
            value={searchForm.requestOrderId ?? ''}
            onChangeValue={(v) => onChangeInput('requestOrderId', v)}
            onClear={() => onChangeInput('requestOrderId', null)}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <SearchTextField
            label="Supplier Order ID"
            size="small"
            fullWidth
            value={searchForm.externalOrderId ?? ''}
            onChangeValue={(v) => onChangeInput('externalOrderId', v)}
            onClear={() => onChangeInput('externalOrderId', null)}
          />
        </Grid>

        {showPartner ? (
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              disablePortal
              options={partnerOptions}
              value={selectedPartner}
              onChange={(_, v) => onChangeInput('partnerId', optionValueToInput(v))}
              getOptionLabel={(opt) => String(opt.label ?? '')}
              isOptionEqualToValue={(a, b) => a.value === b.value}
              renderInput={(params) => (
                <SearchTextField
                  {...params}
                  label="Partner"
                  size="small"
                  fullWidth
                  onClear={() => onChangeInput('partnerId', null)}
                />
              )}
            />
          </Grid>
        ) : (
          <Grid item xs={12} sm={6} md={4} />
        )}
      </Grid>
    </SearchPanel>
  );
};

export default OrdersSearchPanel;
